export type SupportTicketErrorFrame = {
  /** Component or function name when known */
  label: string;
  /** Short app path, e.g. `pages/SupportTicketsPage.tsx · ligne 169` */
  location: string;
};

export type ParsedSupportTicketError = {
  isCrashDump: boolean;
  /** Raw JS error class, e.g. ReferenceError */
  kind: string;
  /** French headline for non-dev readers */
  humanKind: string;
  /** Error message or full user text */
  message: string;
  appFrames: SupportTicketErrorFrame[];
  /** Full text for expandable technical block */
  raw: string;
};

const ERROR_KIND_FR: Record<string, string> = {
  ReferenceError: 'Variable ou fonction introuvable',
  TypeError: 'Opération impossible sur ce type de donnée',
  SyntaxError: 'Erreur de syntaxe dans le code',
  RangeError: 'Valeur hors limites',
  URIError: 'Adresse ou URL invalide',
  EvalError: 'Erreur d’évaluation du code',
};

const DEPENDENCY_FRAME_RE =
  /node_modules|\.vite\/deps\/|webpack:|vite\/dist|@vite\/|\/chunk-[A-Z0-9]+\.js/i;

function humanErrorKind(kind: string): string {
  return ERROR_KIND_FR[kind] ?? (kind ? `Erreur technique (${kind})` : 'Problème signalé');
}

function isDependencyFrame(line: string): boolean {
  return DEPENDENCY_FRAME_RE.test(line);
}

function parseStackLine(line: string): { label: string; href?: string } | null {
  const trimmed = line.trim();
  if (!trimmed.startsWith('at ')) return null;

  const withParen = trimmed.match(/^at\s+(.+?)\s+\(([^)]+)\)\s*$/);
  if (withParen) return { label: withParen[1], href: withParen[2] };

  const bare = trimmed.match(/^at\s+(.+)$/);
  if (bare) return { label: bare[1] };
  return null;
}

function trailingLineCol(href: string): string | undefined {
  const m = href.match(/:(\d+):(\d+)\s*$/);
  return m?.[1];
}

/** ponytail: naive URL parse — good enough for Vite dev + prod `/assets/*.js` paths */
export function friendlySourceLocation(href: string, fallbackLabel?: string): string {
  const path = href.replace(/^https?:\/\/[^/]+/, '');
  const line = trailingLineCol(path);

  const srcIdx = path.indexOf('/src/');
  if (srcIdx >= 0) {
    const rest = path.slice(srcIdx + 5);
    const file = rest.split('?')[0]?.split(':')[0] ?? rest;
    return line ? `${file} · ligne ${line}` : file;
  }

  const app = path.match(/\/apps\/([^/]+)\/src\/([^?]+)/);
  if (app) {
    const [, appName, fileRaw] = app;
    const file = fileRaw.split(':')[0] ?? fileRaw;
    const loc = `${appName}/${file}`;
    return line ? `${loc} · ligne ${line}` : loc;
  }

  const pkg = path.match(/\/packages\/([^/]+)\/src\/([^?]+)/);
  if (pkg) {
    const [, pkgName, fileRaw] = pkg;
    const file = fileRaw.split(':')[0] ?? fileRaw;
    const loc = `${pkgName}/${file}`;
    return line ? `${loc} · ligne ${line}` : loc;
  }

  if (fallbackLabel && !href.includes('node_modules')) return fallbackLabel;
  return fallbackLabel ?? href;
}

export function extractAppFrames(stackText: string): SupportTicketErrorFrame[] {
  if (!stackText.trim()) return [];

  const seen = new Set<string>();
  const frames: SupportTicketErrorFrame[] = [];

  for (const line of stackText.split('\n')) {
    if (isDependencyFrame(line)) continue;
    const parsed = parseStackLine(line);
    if (!parsed) continue;

    const location = parsed.href
      ? friendlySourceLocation(parsed.href, parsed.label)
      : friendlySourceLocation(parsed.label, parsed.label);

    if (location.includes('node_modules')) continue;

    const key = `${parsed.label}|${location}`;
    if (seen.has(key)) continue;
    seen.add(key);

    frames.push({ label: parsed.label, location });
  }

  return frames;
}

export function parseSupportTicketError(
  description: string,
  context?: Record<string, unknown> | null,
): ParsedSupportTicketError {
  const rawDescription = description.trim();
  const componentStack =
    typeof context?.componentStack === 'string' ? context.componentStack.trim() : '';

  const parts = rawDescription.split(/\n\n+/);
  const headline = parts[0]?.trim() ?? '';
  const embeddedStack = parts.slice(1).join('\n\n').trim();
  const stackSource = embeddedStack || componentStack;

  const errorMatch = headline.match(/^([A-Za-z]+Error):\s*(.+)$/);
  const kind = errorMatch?.[1] ?? '';
  const message = errorMatch?.[2] ?? headline;
  const hasStack = /^\s*at\s+/m.test(stackSource);
  const isCrashDump = Boolean(errorMatch && (hasStack || embeddedStack || componentStack));

  const appFrames = isCrashDump ? extractAppFrames(stackSource) : [];
  const raw =
    rawDescription +
    (componentStack && !embeddedStack && isCrashDump ? `\n\n${componentStack}` : '');

  return {
    isCrashDump,
    kind,
    humanKind: isCrashDump ? humanErrorKind(kind) : 'Demande',
    message: isCrashDump ? message : rawDescription,
    appFrames,
    raw,
  };
}
